import { useEffect, useRef } from 'react';
import { setClosingConfirmation, tg } from '../utils/telegram';

/**
 * Closing confirmation permanently disabled — Telegram's "changes may be lost"
 * prompt is not used.
 */
export function useClosingConfirmation() {
  useEffect(() => {
    setClosingConfirmation(false);
    return () => setClosingConfirmation(false);
  }, []);
}

/**
 * Fun shake gesture — tiny daily bonus. Server-rate-limited.
 * Does NOT fire during active tapping (cooldown after last tap).
 */
export function useShakeBonus({ enabled = true, onShake } = {}) {
  const lastFire = useRef(0);
  const lastTap = useRef(Date.now());

  useEffect(() => {
    const markTap = () => {
      lastTap.current = Date.now();
    };
    window.addEventListener('pointerdown', markTap, { passive: true });
    return () => window.removeEventListener('pointerdown', markTap);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const w = tg();
    const acc = w?.Accelerometer;
    if (!acc?.start) return undefined;

    let prev = null;
    const onChange = () => {
      if (Date.now() - lastTap.current < 2500) return;
      const x = acc.x || 0;
      const y = acc.y || 0;
      const z = acc.z || 0;
      if (prev) {
        const dx = x - prev.x;
        const dy = y - prev.y;
        const dz = z - prev.z;
        const mag = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (mag > 2.4 && Date.now() - lastFire.current > 4000) {
          lastFire.current = Date.now();
          onShake?.();
        }
      }
      prev = { x, y, z };
    };

    try {
      acc.start({ refresh_rate: 60 }, (ok) => {
        if (!ok) return;
      });
      w.onEvent?.('accelerometerChanged', onChange);
    } catch {
      return undefined;
    }

    return () => {
      try {
        w.offEvent?.('accelerometerChanged', onChange);
        acc.stop?.();
      } catch (_) {
        /* ignore */
      }
    };
  }, [enabled, onShake]);
}
